import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState, } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { startTotpEnrollment, completeTotpEnrollment, getEnrolledTotpFactor, unenrollTotp, } from './mfa.js';
import { validateTotpCode, normalizeTotpCode, getAuthErrorCode, mapMfaErrorMessage, buildTotpDisplayName, } from './mfaPure.js';
import { mfaV1, mfaV2 } from './mfaStyles.js';
/** Asia/Tokyo の YYYY-MM-DD（enrollment 表示名用の cosmetic 値）。 */
function todayJst() {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date());
}
/**
 * S-75: 管理者の 2 段階認証（TOTP）を **任意 (opt-in)** で登録 / 解除する設定 UI。
 *
 * 契約（統合版22 別添① 5.2）は「2FA の利用は管理者が任意に選択できる」と定めるため、
 * ログイン時の強制（grace enrollment）は行わず、本コンポーネントから自発的に登録する。
 * 登録済みの場合のサインイン challenge は SignInPage 側（<MfaChallenge>）が担う。
 *
 * MFA はアカウント単位のため、いずれか 1 つの app（tenant-portal / saas-admin 等）で
 * 登録すれば全管理 app のサインインで challenge が有効になる。
 */
export function MfaSettings({ user, enrolled, issuer, accountName, onChange, variant = 'v1', }) {
    const st = variant === 'v2' ? mfaV2 : mfaV1;
    if (!user)
        return null;
    return (_jsxs("div", { style: st.card, children: [_jsx("h2", { style: st.title, children: "2 \u6BB5\u968E\u8A8D\u8A3C\uFF08\u4EFB\u610F\uFF09" }), _jsx("p", { style: st.subtitle, children: "\u30B5\u30A4\u30F3\u30A4\u30F3\u3092\u8A8D\u8A3C\u30A2\u30D7\u30EA\uFF08Google Authenticator / Microsoft Authenticator \u7B49\uFF09\u306E \u30EF\u30F3\u30BF\u30A4\u30E0\u30B3\u30FC\u30C9\u3067\u4FDD\u8B77\u3057\u307E\u3059\u3002\u767B\u9332\u306F\u4EFB\u610F\u3067\u3059\u3002" }), enrolled ? (_jsx(EnrolledView, { user: user, onChange: onChange, st: st })) : (_jsx(UnenrolledView, { user: user, issuer: issuer, accountName: accountName, onChange: onChange, st: st }))] }));
}
// ---------- 登録済み: 状態表示 + 解除 ----------
function EnrolledView({ user, onChange, st, }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const onUnenroll = useCallback(async () => {
        const factor = getEnrolledTotpFactor(user);
        if (!factor) {
            await onChange();
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await unenrollTotp(user, factor.uid);
            await onChange();
        }
        catch (err) {
            setError(mapMfaErrorMessage(getAuthErrorCode(err)));
        }
        finally {
            // onChange が enrolled=false を反映すれば本 View は unmount される（no-op）。
            // 反映されない場合に「解除中…」固着を防ぐため必ず戻す。
            setBusy(false);
        }
    }, [user, onChange]);
    return (_jsxs("div", { style: { marginTop: '0.75rem' }, children: [_jsxs("p", { style: { ...st.subtitle, marginTop: 0 }, children: [_jsx("strong", { style: { color: st.onColor, fontWeight: 600 }, children: "\u25CF \u6709\u52B9" }), "\uFF08\u8A8D\u8A3C\u30A2\u30D7\u30EA\u3067\u4FDD\u8B77\u4E2D\uFF09"] }), error && (_jsx("p", { role: "alert", style: st.error, children: error })), _jsx("button", { type: "button", onClick: () => {
                    void onUnenroll();
                }, disabled: busy, style: st.secondaryButton, children: busy ? '解除中…' : '2 段階認証を解除する' })] }));
}
// ---------- 未登録: 状態表示 + 登録フロー ----------
function UnenrolledView({ user, issuer, accountName, onChange, st, }) {
    const [start, setStart] = useState(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const startingRef = useRef(false);
    const beginEnrollment = useCallback(async () => {
        if (startingRef.current)
            return;
        startingRef.current = true;
        setBusy(true);
        setError(null);
        try {
            const s = await startTotpEnrollment(user, issuer, accountName);
            setStart(s);
        }
        catch (err) {
            setError(mapMfaErrorMessage(getAuthErrorCode(err)));
            startingRef.current = false;
        }
        finally {
            setBusy(false);
        }
    }, [user, issuer, accountName]);
    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!start)
            return;
        const v = validateTotpCode(code);
        if (v) {
            setError(v);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await completeTotpEnrollment(user, start.secret, normalizeTotpCode(code), buildTotpDisplayName(todayJst()));
            await onChange();
        }
        catch (err) {
            setError(mapMfaErrorMessage(getAuthErrorCode(err)));
        }
        finally {
            // onChange が enrolled=true を反映すれば本 View は unmount される（no-op）。
            // 反映されない場合に「登録中…」固着を防ぐため必ず戻す。
            setBusy(false);
        }
    }, [start, code, user, onChange]);
    // 開始前: ボタンのみ
    if (!start) {
        return (_jsxs("div", { style: { marginTop: '0.75rem' }, children: [_jsx("p", { style: { ...st.subtitle, marginTop: 0 }, children: _jsx("strong", { style: { color: st.offColor, fontWeight: 600 }, children: "\u25CF \u7121\u52B9" }) }), error && (_jsx("p", { role: "alert", style: st.error, children: error })), _jsx("button", { type: "button", onClick: () => {
                        void beginEnrollment();
                    }, disabled: busy, style: st.button(busy), children: busy ? '準備中…' : '2 段階認証を登録する' })] }));
    }
    // 開始後: QR + コード入力
    return (_jsxs("div", { style: { marginTop: '0.75rem' }, children: [_jsx("p", { style: { ...st.subtitle, marginTop: 0 }, children: "\u8A8D\u8A3C\u30A2\u30D7\u30EA\u3067 QR \u3092\u8AAD\u307F\u53D6\u308A\u3001\u8868\u793A\u3055\u308C\u305F 6 \u6841\u30B3\u30FC\u30C9\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044\u3002" }), _jsx("div", { style: st.qrWrap, children: _jsx(QRCodeSVG, { value: start.qrCodeUrl, size: 176, includeMargin: true }) }), _jsx("p", { style: { ...st.subtitle, marginTop: 0 }, children: "QR \u304C\u8AAD\u3081\u306A\u3044\u5834\u5408\u306E\u624B\u52D5\u5165\u529B\u30AD\u30FC:" }), _jsx("div", { style: st.secretBox, children: start.sharedSecretKey }), _jsxs("form", { style: st.form, onSubmit: onSubmit, noValidate: true, children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "mfa-enroll-code", style: st.label, children: "\u78BA\u8A8D\u30B3\u30FC\u30C9" }), _jsx("input", { id: "mfa-enroll-code", inputMode: "numeric", autoComplete: "one-time-code", maxLength: 12, placeholder: "123456", required: true, value: code, onChange: (e) => setCode(e.target.value), style: st.input, "aria-invalid": Boolean(error), "aria-describedby": error ? 'mfa-enroll-error' : undefined })] }), error && (_jsx("p", { id: "mfa-enroll-error", role: "alert", style: st.error, children: error })), _jsx("button", { type: "submit", disabled: busy, style: st.button(busy), children: busy ? '登録中…' : '登録する' })] })] }));
}
//# sourceMappingURL=MfaSettings.js.map